import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Lock,
  Search,
  X,
} from 'lucide-react'
import { type ReactNode, useCallback, useState } from 'react'
import type { SortDir } from '../lib/data-table'
import type { GroupOption } from '../lib/grouped-table'
import { cn } from '../lib/utils'
import { Select } from './ui/select'

export function TableToolbar({
  search,
  onSearch,
  filteredRows,
  totalRows,
  pageSize,
  onPageSize,
  pageSizeOptions = [10, 25, 50, 100],
  rightSlot,
  groupBy,
}: {
  search: string
  onSearch: (q: string) => void
  filteredRows: number
  totalRows: number
  pageSize: number
  onPageSize: (n: number) => void
  pageSizeOptions?: number[]
  rightSlot?: ReactNode
  /** Optional grouping controls rendered between search and the right slot. */
  groupBy?: {
    value: string
    options: GroupOption[]
    onChange: (key: string) => void
    onExpandAll: () => void
    onCollapseAll: () => void
    allCollapsed: boolean
  }
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-2">
      <div className="relative flex-1 min-w-[16rem] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-tertiary pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Suchen…"
          className="w-full h-9 pl-9 pr-9 rounded-lg border border-border-default bg-bg-overlay/40 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent/60 focus:bg-bg-overlay focus:ring-2 focus:ring-accent/20"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearch('')}
            aria-label="Suche zurücksetzen"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-tertiary hover:text-accent rounded"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {/* flex-wrap: greift nur, wenn die Controls nicht in die Zeile passen
          (mobil <640) — ab 640 passt die Gruppe wie bisher einzeilig, das
          Wrap ist dort ein No-op. gap-y-2 hält die Zeilen im Wrap-Fall eng. */}
      <div className="flex flex-wrap items-center gap-3 gap-y-2 text-xs text-text-tertiary">
        {groupBy && (
          <span className="inline-flex items-center gap-1.5">
            <label htmlFor="dt-groupby">Gruppieren</label>
            <Select
              id="dt-groupby"
              value={groupBy.value}
              onChange={(e) => groupBy.onChange(e.target.value)}
              className="h-8 w-32 text-xs"
            >
              {groupBy.options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </Select>
            {groupBy.value !== 'none' && (
              <button
                type="button"
                onClick={groupBy.allCollapsed ? groupBy.onExpandAll : groupBy.onCollapseAll}
                className="h-8 px-2 inline-flex items-center gap-1 rounded-md text-text-tertiary hover:text-accent hover:bg-bg-overlay/40 transition-colors"
                title={groupBy.allCollapsed ? 'Alle Gruppen einklappen' : 'Alle Gruppen ausklappen'}
              >
                {groupBy.allCollapsed ? (
                  <>
                    <ChevronDown className="h-3.5 w-3.5" />
                    Alle einklappen
                  </>
                ) : (
                  <>
                    <ChevronUp className="h-3.5 w-3.5" />
                    Alle ausklappen
                  </>
                )}
              </button>
            )}
          </span>
        )}
        <span className="tabular-nums">
          {search.trim() ? `${filteredRows} von ${totalRows}` : `${totalRows} Einträge`}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <label htmlFor="dt-pagesize">Pro Seite</label>
          <Select
            id="dt-pagesize"
            value={String(pageSize)}
            onChange={(e) => onPageSize(Number(e.target.value))}
            className="h-8 w-20 text-xs"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </span>
        {rightSlot}
      </div>
    </div>
  )
}

/**
 * Sticky-ish group header rendered inside <tbody>. Clicking the chevron
 * toggles collapse; the inline checkbox (if `onToggleAll` is provided)
 * lets admin select every row in the group with one click. `colSpan`
 * must match the table's column count.
 */
export function GroupHeaderRow({
  label,
  count,
  restrictedCount,
  collapsed,
  onToggle,
  colSpan,
  groupChecked,
  groupIndeterminate,
  onToggleAll,
  color,
  muted,
}: {
  label: string
  count: number
  restrictedCount: number
  collapsed: boolean
  onToggle: () => void
  colSpan: number
  /** When provided, render a select-all checkbox for this group. */
  groupChecked?: boolean
  groupIndeterminate?: boolean
  onToggleAll?: () => void
  /** Optional tailwind color name for a small swatch in front of the label. */
  color?: string | null
  /** Muted styling — used for the synthetic "Nicht gruppiert" bucket. */
  muted?: boolean
}) {
  return (
    <tr className="bg-bg-overlay/40 sticky top-0 z-[1]">
      <td colSpan={colSpan} className="px-2 py-1.5">
        <div className="flex items-center gap-2">
          {onToggleAll ? (
            <span className="pl-3 pr-1">
              <input
                type="checkbox"
                checked={!!groupChecked}
                ref={(el) => {
                  if (el) el.indeterminate = !!groupIndeterminate
                }}
                onChange={onToggleAll}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Alle in „${label}“ auswählen`}
                className="accent-accent h-4 w-4 cursor-pointer"
              />
            </span>
          ) : (
            <span className="pl-3" />
          )}
          <button
            type="button"
            onClick={onToggle}
            className={cn(
              'flex items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-wider hover:text-text-primary',
              muted
                ? 'text-text-tertiary italic font-medium normal-case tracking-normal'
                : 'text-text-secondary',
            )}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            {color && !muted && (
              <span
                aria-hidden="true"
                className={cn('inline-block h-2 w-2 rounded-full', groupColorClass(color))}
              />
            )}
            <span>{label}</span>
            <span className="text-text-tertiary tabular-nums font-normal">({count})</span>
            {restrictedCount > 0 && (
              <span
                className="inline-flex items-center gap-0.5 text-2xs text-accent tabular-nums font-normal"
                title={`${restrictedCount} mit beschränktem Zugriff`}
              >
                <Lock className="h-2.5 w-2.5" />({restrictedCount} gesperrt)
              </span>
            )}
          </button>
        </div>
      </td>
    </tr>
  )
}

/**
 * Map a stored color name (one of the palette in ManualGroupPanel) to a
 * tailwind background-color utility. Unknown colors fall back to a muted
 * tertiary tone. Kept here so both the panel and the header pick the
 * same shade.
 */
export function groupColorClass(color: string | null | undefined): string {
  switch (color) {
    case 'red':
      return 'bg-red-500'
    case 'orange':
      return 'bg-orange-500'
    case 'amber':
      return 'bg-amber-500'
    case 'emerald':
      return 'bg-emerald-500'
    case 'sky':
      return 'bg-sky-500'
    case 'indigo':
      return 'bg-indigo-500'
    case 'violet':
      return 'bg-violet-500'
    case 'rose':
      return 'bg-rose-500'
    default:
      return 'bg-text-tertiary/60'
  }
}

export function SortableHeader({
  label,
  sortKey,
  currentKey,
  currentDir,
  onSort,
  align = 'left',
  className,
}: {
  label: string
  sortKey: string
  currentKey: string | null
  currentDir: SortDir
  onSort: (key: string) => void
  align?: 'left' | 'right' | 'center'
  className?: string
}) {
  const active = currentKey === sortKey
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={cn(
        'inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-medium transition-colors',
        active ? 'text-accent' : 'text-text-tertiary hover:text-text-secondary',
        align === 'right' && 'flex-row-reverse',
        className,
      )}
    >
      <span>{label}</span>
      {!active && <ArrowUpDown className="h-3 w-3 opacity-60" />}
      {active && currentDir === 'asc' && <ArrowUp className="h-3 w-3" />}
      {active && currentDir === 'desc' && <ArrowDown className="h-3 w-3" />}
    </button>
  )
}

export function Pager({
  page,
  totalPages,
  onPage,
  filteredRows,
  pageSize,
}: {
  page: number
  totalPages: number
  onPage: (p: number) => void
  filteredRows: number
  pageSize: number
}) {
  if (totalPages <= 1) return null
  const start = filteredRows === 0 ? 0 : (page - 1) * pageSize + 1
  const end = Math.min(filteredRows, page * pageSize)
  return (
    <div className="flex items-center justify-between gap-3 py-3 text-sm text-text-secondary">
      <span className="tabular-nums">
        {start}–{end} von {filteredRows}
      </span>
      <nav className="flex items-center gap-1" aria-label="Seitennavigation">
        <button
          type="button"
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={page === 1}
          aria-label="Vorherige Seite"
          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-text-secondary hover:text-text-primary hover:bg-bg-overlay disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {pageNumbers(page, totalPages).map((p, idx, arr) => {
          if (p === '…') {
            // Stable key: anchor on the neighboring real page number
            const anchor = idx === 1 ? arr[0] : arr[arr.length - 2]
            return (
              <span
                key={`gap-${anchor}`}
                className="inline-flex items-center justify-center h-8 w-8 text-text-tertiary"
              >
                …
              </span>
            )
          }
          return (
            <button
              key={p}
              type="button"
              onClick={() => onPage(p)}
              aria-current={p === page ? 'page' : undefined}
              className={cn(
                'inline-flex items-center justify-center h-8 min-w-8 px-2 rounded-md text-sm tabular-nums transition-colors',
                p === page
                  ? 'bg-accent text-bg-base font-semibold'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-overlay',
              )}
            >
              {p}
            </button>
          )
        })}
        <button
          type="button"
          onClick={() => onPage(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          aria-label="Nächste Seite"
          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-text-secondary hover:text-text-primary hover:bg-bg-overlay disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </nav>
    </div>
  )
}

/**
 * Compact page selector: 1 … 4 5 [6] 7 8 … 12
 * Shows first + last + 2 around current, with ellipses.
 */
function pageNumbers(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const out: (number | '…')[] = []
  out.push(1)
  if (current > 4) out.push('…')
  const start = Math.max(2, current - 2)
  const end = Math.min(total - 1, current + 2)
  for (let p = start; p <= end; p++) out.push(p)
  if (current < total - 3) out.push('…')
  out.push(total)
  return out
}

export function EmptyTable({
  message,
  hint,
  action,
}: {
  message: string
  hint?: string
  /** Optionaler CTA direkt im Empty-State (statt Verweis auf entfernte Buttons). */
  action?: ReactNode
}) {
  return (
    <div className="px-6 py-12 text-center text-text-tertiary">
      <p className="text-sm">{message}</p>
      {hint && <p className="text-xs mt-1 text-text-tertiary/70">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  )
}

/**
 * Header checkbox that supports "all visible selected" + indeterminate state
 * (some-but-not-all selected). Visual style matches row checkboxes.
 */
export function SelectAllCheckbox({
  checked,
  indeterminate,
  onToggle,
  label = 'Alle auf dieser Seite auswählen',
}: {
  checked: boolean
  indeterminate: boolean
  onToggle: () => void
  label?: string
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = indeterminate
      }}
      onChange={onToggle}
      aria-label={label}
      className="accent-accent h-4 w-4 cursor-pointer"
    />
  )
}

export function RowCheckbox({
  checked,
  onToggle,
  label = 'Zeile auswählen',
}: {
  checked: boolean
  onToggle: () => void
  label?: string
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={onToggle}
      aria-label={label}
      onClick={(e) => e.stopPropagation()}
      className="accent-accent h-4 w-4 cursor-pointer"
    />
  )
}

/**
 * Floating bottom-of-viewport action bar shown when ≥1 rows are selected.
 * `onSelectAllFiltered` (optional) shows a “alle X Treffer auswählen” shortcut
 * when the user has only ticked the visible page but the filtered set is bigger.
 */
export function BulkActionBar({
  selectionCount,
  filteredRows,
  onClear,
  onSelectAllFiltered,
  children,
}: {
  selectionCount: number
  filteredRows?: number
  onClear: () => void
  onSelectAllFiltered?: () => void
  children: ReactNode
}) {
  if (selectionCount === 0) return null
  const showSelectAll =
    onSelectAllFiltered != null && typeof filteredRows === 'number' && filteredRows > selectionCount
  return (
    <div className="sticky bottom-4 z-20 flex justify-center pointer-events-none">
      <section
        className="pointer-events-auto inline-flex items-center gap-3 rounded-full border border-border-default bg-bg-elevated/95 backdrop-blur-md shadow-lg px-3 py-1.5"
        aria-label="Mehrfachauswahl"
      >
        <span className="text-sm tabular-nums text-text-primary pl-2">
          <strong className="font-semibold">{selectionCount}</strong> ausgewählt
        </span>
        {showSelectAll && (
          <button
            type="button"
            onClick={onSelectAllFiltered}
            className="text-xs text-accent hover:underline"
          >
            alle {filteredRows} Treffer auswählen
          </button>
        )}
        <span className="h-5 w-px bg-border-subtle" />
        {children}
        <span className="h-5 w-px bg-border-subtle" />
        <button
          type="button"
          onClick={onClear}
          aria-label="Auswahl aufheben"
          className="p-1.5 text-text-tertiary hover:text-text-primary hover:bg-bg-overlay rounded-full transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </section>
    </div>
  )
}

/** Compact "‹ N/M ›" pager for narrow contexts (calendar info-panel,
 *  embedded dashboards). Renders nothing when totalPages <= 1. */
export function MiniPager({
  page,
  totalPages,
  onPage,
  pageSize,
  onPageSize,
  pageSizeOptions = [5, 10, 25, 50],
}: {
  page: number
  totalPages: number
  onPage: (p: number) => void
  /** When provided alongside onPageSize, renders a compact size picker
   *  next to the pager. The bar shows even on single-page lists so users
   *  can shrink the page size. */
  pageSize?: number
  onPageSize?: (n: number) => void
  pageSizeOptions?: number[]
}) {
  const hasSizePicker = typeof pageSize === 'number' && typeof onPageSize === 'function'
  if (totalPages <= 1 && !hasSizePicker) return null
  return (
    <div className="flex items-center justify-center gap-1 py-2 shrink-0">
      {totalPages > 1 && (
        <>
          <button
            type="button"
            onClick={() => onPage(Math.max(1, page - 1))}
            disabled={page === 1}
            aria-label="Vorherige Seite"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:text-text-primary hover:bg-bg-overlay disabled:opacity-30"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="text-xs tabular-nums text-text-secondary px-2">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            aria-label="Nächste Seite"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:text-text-primary hover:bg-bg-overlay disabled:opacity-30"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </>
      )}
      {hasSizePicker && (
        <select
          value={String(pageSize)}
          onChange={(e) => onPageSize(Number(e.target.value))}
          aria-label="Einträge pro Seite"
          title="Einträge pro Seite"
          className="ml-1 h-7 rounded-md border border-border-default bg-bg-elevated px-1.5 text-xs text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent"
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n}/Seite
            </option>
          ))}
        </select>
      )}
    </div>
  )
}

/** Persistent page-size state for embedded list bodies in CalendarInfoPanel.
 *  Each tab gets its own key so user choices stick per section. */
export function useEmbeddedPageSize(key: string, defaultSize: number) {
  const storageKey = `embedded-page-size:${key}`
  const [size, setSize] = useState<number>(() => {
    if (typeof window === 'undefined') return defaultSize
    const raw = window.localStorage.getItem(storageKey)
    const parsed = raw ? Number(raw) : Number.NaN
    return Number.isFinite(parsed) && parsed > 0 ? parsed : defaultSize
  })
  const setAndPersist = useCallback(
    (n: number) => {
      setSize(n)
      try {
        window.localStorage.setItem(storageKey, String(n))
      } catch {
        // localStorage may be unavailable (private mode, quota, etc.) — fall
        // back to in-memory state. Not worth notifying the user.
      }
    },
    [storageKey],
  )
  return [size, setAndPersist] as const
}
